import { ToolMaterialId } from 'constants/tool-materials'
import {
  ToolTypeId,
  canSidemill,
  getNameFromToolTypeId,
} from 'constants/tool-types'
import { unitFromString } from 'lib/enum'
import FusionTool from 'types/fusion-tool'
import { Prisma, ToolRole, Unit } from '@prisma/client'

// The geometry part of the Fusion JSON data (like SFDM, LCF, OAL) is based on ISO 13399.
// The standard itself is available behind a paywall on https://www.iso.org/standard/36757.html, but
// Seco tools has a quick list of the attribute names on https://www.secotools.com/article/120536.
// Also, the MDES specification from ModuleWorks use the ISO 13399 names where applicable, so the
// MDES specification (which is freely available in both PDF and XSD) is a good source on this:
// https://mdes.info/en/specification/

const degreesToRads = (deg) => (deg * Math.PI) / 180.0

// original map keys are fusion materials and values are toolpath materials
const TOOLMATERIAL_MAP: Record<string, ToolMaterialId> = {
  carbide: ToolMaterialId.Carbide,
  ceramics: ToolMaterialId.Ceramic,
  unspecified: ToolMaterialId.Unspecified,
  'ti coated': ToolMaterialId.TiCoated,
  hss: ToolMaterialId.HSS,
}

// original map keys are fusion tool types and values are toolpath tool types
// NOTE: Make sure this list is in sync with supportedTypeEnumValues in tool_v1.ts
export const TOOLTYPE_MAP: Record<string, ToolTypeId> = {
  'flat end mill': ToolTypeId.FlatEndmill,
  'radius mill': ToolTypeId.RadiusEndmill,
  'bull nose end mill': ToolTypeId.RadiusEndmill,
  'ball end mill': ToolTypeId.BallEndmill,
  'chamfer mill': ToolTypeId.ChamferMill,
  'spot drill': ToolTypeId.Drill,
  'face mill': ToolTypeId.FaceMill,
  'tap right hand': ToolTypeId.CuttingTap,
  'tap left hand': ToolTypeId.CuttingTap, // sets clockwiseRotation property to false to indicate left handed
  'slot mill': ToolTypeId.KeyseatCutter,
  drill: ToolTypeId.Drill,
  'center drill': ToolTypeId.Drill,
  reamer: ToolTypeId.Reamer,
  'boring bar': ToolTypeId.BoringBar,
  'counter bore': ToolTypeId.CounterBore,
  'dovetail mill': ToolTypeId.DovetailCutter,
  'counter sink': ToolTypeId.CounterSink,
  'thread mill': ToolTypeId.ThreadMill,
}

export function defaultToolName(
  toolNo: number,
  description: string,
  typeId: keyof typeof TOOLTYPE_MAP,
): string {
  let name = ''

  if (toolNo) {
    name += `T${toolNo} `
  }

  if (description) {
    name += `${description} `
  } else {
    if (typeId) {
      name += `${getNameFromToolTypeId(TOOLTYPE_MAP[typeId])} `
    }
  }

  return name.trim()
}

export const fusionToolsToToolpathTools = (
  fusionTools: Array<FusionTool>,
  ownerId?: number,
  teamId?: number,
): {
  compatibleTools: Array<Omit<Prisma.ToolCreateManyInput, 'uuid'>>
  incompatibleTools: Array<FusionTool>
} => {
  const compatibleTools = []
  const incompatibleTools = []
  const seenGuids = new Set<string>()

  const getCanSideMill = (fusionTool: FusionTool): boolean => {
    // If a face mill and has a taper angle > 0, don't allow
    if (fusionTool.type === 'face mill') {
      const taperAngle = getTipAngle(fusionTool)
      if (taperAngle > 0.0001) {
        return false
      }
    }

    const typeId = TOOLTYPE_MAP[fusionTool.type]
    return canSidemill(typeId)
  }

  const getCornerRadius = (fusionTool: FusionTool): number => {
    if (fusionTool.type === 'ball end mill') {
      return (fusionTool.geometry.DC || 0) / 2
    }

    return fusionTool.geometry.RE || 0
  }

  const getBottomDiameter = (fusionTool: FusionTool): number => {
    if (fusionTool.type === 'slot mill') {
      return (fusionTool.geometry.DC - getShankDiameter(fusionTool)) / 2
    }

    return fusionTool.geometry['tip-diameter'] || 0
  }

  const getMaxDiameter = (fusionTool: FusionTool): number => {
    if (fusionTool.type === 'face mill' || fusionTool.type === 'tapered mill') {
      const shoulderLength = fusionTool.geometry['shoulder-length']
      const taperAngle = getTipAngle(fusionTool)
      const extraRadius = Math.tan(degreesToRads(taperAngle)) * shoulderLength
      return fusionTool.geometry.DC + extraRadius * 2
    } else {
      return fusionTool.geometry.DC || 0.0
    }
  }

  const intersectHorizontalLine = (
    horizontalY: number,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
  ): number => {
    if (minY > horizontalY && maxY > horizontalY) {
      return
    }

    if (minY < horizontalY && maxY < horizontalY) {
      return
    }

    if (minY === horizontalY) {
      return minX
    }

    return minX + ((horizontalY - minY) * (maxX - minX)) / (maxY - minY)
  }

  const calculateShaftHeightUntilMaxDiameter = (
    segments: FusionTool['shaft']['segments'],
    maxDiameter: number,
  ): number => {
    let isBottom: boolean = false
    let height: number
    let minX = 0

    for (let i = 0; i < segments.length; i++) {
      let segment = segments[i]
      let maxX = minX + segment['height']
      let minY = segment['lower-diameter']
      let maxY = segment['upper-diameter']

      if (minY === maxY) {
        return maxX
      }

      if (isBottom && minY === maxDiameter && maxY < maxDiameter) {
        minY = minY - Number.EPSILON
      }

      if (segment['lower-diameter'] >= maxDiameter) {
        height = minX
        break
      }

      height = intersectHorizontalLine(maxDiameter, minX, maxX, minY, maxY)

      if (!height) {
        break
      }

      minX = maxX
      isBottom = false
    }

    return !height ? minX : height
  }

  const getNeckLength = (fusionTool: FusionTool): number => {
    const fluteLength = fusionTool.geometry.LCF
    const stickout = fusionTool.geometry.LB
    const shouldLength = fusionTool.geometry['shoulder-length']

    if (fusionTool.shaft) {
      let segments = fusionTool.shaft.segments
      let shaftNeckLength = calculateShaftHeightUntilMaxDiameter(
        segments,
        fusionTool.geometry.DC,
      )
      const baseNextLength =
        fusionTool.geometry['shoulder-length'] - fluteLength

      const neckLength = shaftNeckLength + baseNextLength
      const shoulderLength = stickout - fluteLength - neckLength

      if (shoulderLength < 0) {
        return stickout - fluteLength
      }

      return neckLength
    } else if (stickout && fluteLength) {
      // JSG NOTE: In fusion, if the should length is longer than the flute then the
      //           difference is treated as if its a necked endmill, even though the diameter is not
      //           explicitely reduced in the geometry definition
      return shouldLength - fluteLength
    }

    return 0
  }

  const getShankDiameter = (fusionTool: FusionTool): number => {
    let nominalShankDiameter = 0
    if (
      fusionTool.shaft?.segments?.[0]?.['lower-diameter'] &&
      fusionTool.geometry.SFDM
    ) {
      nominalShankDiameter = Math.min(
        fusionTool.shaft.segments[0]['lower-diameter'],
        fusionTool.geometry.SFDM,
      )
    } else if (!fusionTool.geometry.SFDM) {
      nominalShankDiameter =
        fusionTool.shaft?.segments?.[0]?.['lower-diameter'] || 0
    } else if (fusionTool.geometry.SFDM) {
      nominalShankDiameter = fusionTool.geometry.SFDM
    }

    const neckLength = getNeckLength(fusionTool)
    const nominalDiameter = fusionTool.geometry.DC || 0

    if (neckLength > 0 && nominalShankDiameter >= nominalDiameter) {
      return nominalShankDiameter * 0.97
    }

    return nominalShankDiameter
  }

  const getShoulderDiameter = (fusionTool: FusionTool): number => {
    if (!fusionTool.shaft || fusionTool.shaft.segments.length === 0) {
      // No custom shaft - just use the shaft (aka shank) diameter
      return fusionTool?.geometry?.SFDM || 0
    }

    // Look at segments finding the widest point below the holder

    let nominalShoulderDiameter = 0
    let height = fusionTool.geometry['shoulder-length']
    for (const segment of fusionTool.shaft.segments) {
      nominalShoulderDiameter = Math.max(
        segment['lower-diameter'] || 0,
        nominalShoulderDiameter,
      )

      if (height + segment.height > fusionTool.geometry.LB) {
        // Handle case where an angled part of the shaft goes inside the body (LB = stickout aka "body length")
        let minX = 0.0
        let maxX = minX + segment['height']
        let minY = segment['lower-diameter']
        let maxY = segment['upper-diameter']

        let interestX = fusionTool.geometry.LB - height

        // calculate diameter at intersection
        let interestY = ((maxY - minY) / (maxX - minX)) * interestX
        return Math.max(nominalShoulderDiameter, minY + interestY)
      }

      height += segment.height

      nominalShoulderDiameter = Math.max(
        segment['upper-diameter'],
        nominalShoulderDiameter,
      )
    }

    return nominalShoulderDiameter
  }

  const getTipAngle = (fusionTool: FusionTool): number => {
    if (
      fusionTool.type === 'drill' ||
      fusionTool.type === 'spot drill' ||
      fusionTool.type === 'counter sink'
    ) {
      return fusionTool.geometry.SIG || 0
    }

    return fusionTool.geometry.TA || 0
  }

  const getLeadAngle = (fusionTool: FusionTool): number => {
    return 90 - (fusionTool.geometry.TA || 0)
  }

  const getName = (fusionTool: FusionTool): string => {
    const name = defaultToolName(
      fusionTool?.['post-process']?.['number'],
      fusionTool.description,
      fusionTool.type,
    )

    if (name === '') {
      return fusionTool.type
    }

    return name.trim()
  }

  const getClockwiseRotation = (fusionTool: FusionTool): boolean => {
    if (fusionTool.type === 'tap left hand') {
      return false
    }
    if (fusionTool.type === 'tap right hand') {
      return true
    }

    return fusionTool.geometry.HAND // usually undefined
  }

  const getVendorInfo = (fusionTool: FusionTool) => {
    return {
      vendor: fusionTool.vendor || '',
      productId: fusionTool['product-id'] || '',
      link: fusionTool['product-link'] || '',
    }
  }

  fusionTools.forEach((fusionTool) => {
    if (fusionTool.guid) {
      if (seenGuids.has(fusionTool.guid)) {
        throw new Error(`Duplicate tool guid: ${fusionTool.guid}`)
      }
      seenGuids.add(fusionTool.guid)
    }

    const toolMaterialId = TOOLMATERIAL_MAP[fusionTool.BMC]
    const typeId = TOOLTYPE_MAP[fusionTool.type]
    if (!toolMaterialId || !typeId) {
      incompatibleTools.push(fusionTool)
      return
    }

    const unit = unitFromString(fusionTool.unit)

    const toolToCreate: Omit<Prisma.ToolCreateManyInput, 'uuid'> = {
      name: getName(fusionTool),
      unit,
      diameter: getMaxDiameter(fusionTool), // dynamic
      nominalDiameter: fusionTool.geometry.DC || 0,
      threadLength: fusionTool.geometry.LCF || 0,
      threadPitch: fusionTool.geometry.TP || 0,
      stickout: fusionTool.geometry.LB || 0,
      fluteLength: fusionTool.geometry.LCF || 0,
      fluteCount: fusionTool.geometry.NOF || 0,
      tipAngle: getTipAngle(fusionTool) || 0, // maps
      leadAngle: getLeadAngle(fusionTool), // redundant with tipAngle
      toolHolderBottomDiameter: unit === Unit.INCHES ? 1 : 25.4,
      maximumRpm: 0,
      bottomDiameter: getBottomDiameter(fusionTool),
      shankDiameter: getShankDiameter(fusionTool), // looks at shaft segments
      shoulderDiameter: getShoulderDiameter(fusionTool), // looks at shaft segments
      neckLength: getNeckLength(fusionTool) || 0, // looks at shaft segments
      cornerRadius: getCornerRadius(fusionTool),
      clockwiseRotation: getClockwiseRotation(fusionTool), // has special case for LH tap
      // some versions of Fusion (incorrectly) saves threadProfileAngle+numberOfTeeth on *many* tool types, so explicitly check for typeId here
      threadProfileAngle:
        typeId == ToolTypeId.ThreadMill
          ? fusionTool.geometry['thread-profile-angle']
          : undefined,
      numberOfTeeth:
        typeId == ToolTypeId.ThreadMill ? fusionTool.geometry.NT : undefined,
      typeId,
      sideMill: getCanSideMill(fusionTool), // can calculate
      toolMaterialId,
      role:
        fusionTool.type === 'spot drill'
          ? ToolRole.SPOTTING
          : fusionTool.type === 'center drill'
          ? ToolRole.CENTER_DRILL
          : ToolRole.GENERAL_PURPOSE,
      ownerId,
      teamId,
      guid: fusionTool.guid,
      data: fusionTool,
      ...getVendorInfo(fusionTool),
    }

    compatibleTools.push(toolToCreate)
  })

  return {
    compatibleTools,
    incompatibleTools,
  }
}
